import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import { useNavigate } from 'react-router-dom';
import { useRequestData } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import {
	setDrivetrain,
	setCab,
	setPrice,
	setPaint,
	setPackages,
	setVehicleConfiguration,
	setImage,
	setTrimId
} from '../actions/setVehicleConfiguration';
import {
	setEnableContinueBtn,
	setDisableContinueBtn
} from '../actions/setContinueBtn';
import { initVehicleConfiguration } from '../server/data/defaults';
import { formatter } from '../helpers';
import Select from './Select';
import stepController from '../controllers/StepController';

const Configuration = ({ setCurrentStep }) => {
	setCurrentStep('configuration');
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { trimData, enableContinueBtn } = useSelector((state) => state);
	const { vehicleConfiguration, rootDir } = useSelector((state) => state);
	const [deDupedTrimData, setDeDupedTrimData] = useState([]);
	const [selectedIndex, setSelectedIndex] = useState(null);
	const [hasCab, setHasCab] = useState(false);
	const { widgetName, windowId } = useRequestData();
	const dispatchDriveAndCabData = (itemIndex) => {
		setSelectedIndex(itemIndex);
		const { price, driveTrain, cabType } = deDupedTrimData[itemIndex];
		dispatch(setPrice(price));
		const formattedPrice = formatter.format(price);
		dispatch(
			setDrivetrain({
				value: driveTrain,
				keyLabel: driveTrain,
				valueLabel: formattedPrice,
				modified: true
			})
		);
		if (cabType) {
			dispatch(
				setCab({
					value: cabType,
					keyLabel: cabType,
					valueLabel: '—',
					modified: true
				})
			);
		}

		const displayValue = cabType ? `${driveTrain} ${cabType}` : driveTrain;

		trackEvent(widgetName, windowId, {
			element: 'Configuration option',
			elementCTA: displayValue,
			action: 'selected',
			result: 'Configuration radio button selected'
		});

		stepController.updateLsVehicleConfig();
	};

	const handleSelect = (event, itemIndex) => {
		if (event.target.checked) {
			dispatch(setEnableContinueBtn(true));
			setSelectedIndex(itemIndex);
		}
		dispatchDriveAndCabData(itemIndex);
		if (vehicleConfiguration.options.paint !== '') {
			dispatch(
				setPaint(cloneDeep(initVehicleConfiguration.options.paint))
			);
		}
		if (initVehicleConfiguration.options.packages.selections.length) {
			dispatch(
				setPackages(
					cloneDeep(initVehicleConfiguration.options.packages)
				)
			);
		}
		dispatch(setImage(deDupedTrimData[itemIndex].imageUrl));
		dispatch(setTrimId(deDupedTrimData[itemIndex].id));
		stepController.updateLsVehicleConfig();
	};

	const deDupeTrims = (trims) => {
		const comboMatches = [];
		Object.values(trims).forEach((singleTrimObj) => {
			if (singleTrimObj.trim === vehicleConfiguration.trimName) {
				if (singleTrimObj.driveTrain && singleTrimObj.cabType) {
					setHasCab(true);
					const trimCombo = `${singleTrimObj.driveTrain}:${singleTrimObj.cabType}`;
					if (!comboMatches.includes(trimCombo)) {
						comboMatches.push(trimCombo);
						deDupedTrimData.push(singleTrimObj);
					}
				} else if (singleTrimObj.driveTrain) {
					if (!comboMatches.includes(singleTrimObj.driveTrain)) {
						comboMatches.push(singleTrimObj.driveTrain);
						deDupedTrimData.push(singleTrimObj);
					}
				}
			}
		});
		return deDupedTrimData;
	};

	useEffect(() => {
		if (Object.keys(trimData).length === 0) {
			const lsVehicleConfig = stepController.getLsVehicleConfig();
			if (
				!lsVehicleConfig ||
				!lsVehicleConfig.make ||
				!lsVehicleConfig.model
			) {
				navigate(stepController.getRootPath());
			} else {
				dispatch(setVehicleConfiguration(lsVehicleConfig));
				const trimScreenPath = `/${rootDir}/${lsVehicleConfig.make}/${lsVehicleConfig.model}.htm`;
				navigate(trimScreenPath);
			}
			// stop execution of this effect
			return;
		}
		setDeDupedTrimData(deDupeTrims(trimData));
		dispatch(setDisableContinueBtn(true));
		stepController.updateLsVehicleConfig();

		const driveTrainValue = vehicleConfiguration.options.drivetrain
			? vehicleConfiguration.options.drivetrain.value
			: null;
		const cabValue = vehicleConfiguration.options.cab
			? vehicleConfiguration.options.cab.value
			: null;

		let findIndex = null;
		const deDepedTrimDataLength = deDupedTrimData.length || 0;
		if (deDepedTrimDataLength === 1) {
			dispatch(setTrimId(deDupedTrimData[0].id));
		}
		if (driveTrainValue && cabValue) {
			findIndex = deDupedTrimData.findIndex(
				(trim) =>
					trim?.driveTrain ===
						vehicleConfiguration?.options?.drivetrain?.value &&
					trim?.cabType === vehicleConfiguration?.options?.cab?.value
			);
			setSelectedIndex(findIndex);
			dispatch(setEnableContinueBtn(true));
		} else if (
			driveTrainValue &&
			!cabValue &&
			deDepedTrimDataLength !== 1
		) {
			findIndex = deDupedTrimData.findIndex(
				(trim) =>
					trim?.driveTrain ===
					vehicleConfiguration?.options?.drivetrain?.value
			);
			setSelectedIndex(findIndex);
			dispatch(setEnableContinueBtn(true));
		}

		if (
			!driveTrainValue &&
			enableContinueBtn &&
			deDepedTrimDataLength !== 1
		) {
			dispatch(setDisableContinueBtn(true));
		} else if (deDepedTrimDataLength === 1) {
			dispatchDriveAndCabData(0);
			dispatch(setEnableContinueBtn(true));
		}

		// reset price
		const trimAtIndex = deDupedTrimData[findIndex || 0];
		if (trimAtIndex) {
			dispatch(setPrice(trimAtIndex.price));
		}

		// reset packages
		dispatch(
			setPackages(cloneDeep(initVehicleConfiguration.options.packages))
		);
	}, []);

	return (
		<React.Fragment>
			<form>
				{deDupedTrimData.map((data, index) => {
					const priceDiff = data.price - vehicleConfiguration.price;
					const sign = priceDiff >= 0 ? '+' : '-';
					const detail = `${sign} ${formatter.format(
						Math.abs(priceDiff)
					)}`;
					const displayValue = hasCab
						? `${data.driveTrain} ${data.cabType}`
						: data.driveTrain;
					// We need to come up with a better method of data handling here
					const updatedData = {
						...data,
						value: displayValue,
						id: `${data.id}_configuration`
					};
					return (
						<Select
							data={updatedData}
							detail={detail}
							inputType="radio"
							itemIndex={index}
							selected={index === selectedIndex}
							handleSelect={handleSelect}
							key={`${data.id}.select.configuration`}
						/>
					);
				})}
			</form>
		</React.Fragment>
	);
};

Configuration.propTypes = {
	setCurrentStep: PropTypes.func
};

export default Configuration;
